import { asyncStoreGet, asyncStoreClear } from '../../ui/Utils/asyncStorage'; // adjust path
import { TOKEN_KEY } from '../../ui/contants/authKeys';
import { getDecodedToken } from '../../ui/Utils/tokenUtils';

/**
 * Enhanced Fetch function with proper JSON parsing and error handling
 */
export const Fetch = async (
    url,
    method = 'GET',
    headers = {},
    params = null,
    screenName = "defaultScreen",
    showResponse = false
) => {
    try {
        // Default headers
        const defaultHeaders = {
            'Content-Type': 'application/json',
            ...headers
        };

        // attach token if present
        const token = await asyncStoreGet(TOKEN_KEY);
        if (token) defaultHeaders['Authorization'] = `Bearer ${token}`;

        // send cookies to API (used when backend sets HttpOnly cookie)
        const fetchParams = {
            method: method.toUpperCase(),
            headers: defaultHeaders,
            // credentials: 'include'  // sends cookies
        };

        // Add body for POST/PUT etc., not GET
        if (params && method.toUpperCase() !== 'GET') {
            fetchParams.body = typeof params === 'string' ? params : JSON.stringify(params);
        }

        console.log(`[${screenName}] ${method} ${url}`, { params });

        const response = await fetch(url, fetchParams);

        // Read response body once as text, then try JSON.parse; fallback to raw text
        let text;
        try {
            text = await response.text();
        } catch (e) {
            console.warn('[Fetch] Failed to read response as text', e);
            text = '';
        }

        let parsed;
        try {
            parsed = text ? JSON.parse(text) : null;
        } catch (e) {
            // Not JSON — keep raw text
            parsed = text;
        }

        // If unauthorized, clear stored auth and return a legacy-friendly object
        if (response.status === 401) {
            const body401 = parsed || { message: 'Unauthorized' };
            try {
                await asyncStoreClear();
                console.debug('[Fetch] 401 received - cleared stored auth_token');
            } catch (e) {
                console.warn('[Fetch] failed to clear storage on 401', e);
            }
            return {
                success: false,
                status: 401,
                error: 'Unauthorized',
                response: body401,
                text: async () => (typeof body401 === 'string' ? body401 : JSON.stringify(body401)),
            };
        }

        // Use parsed response body for further handling
        const body = parsed;

        // If HTTP success (2xx)
        if (response.ok) {
            // Common backend success shapes: { code: 0, data: ... } or { success: true, data: ... }
            if (body && (body.code === 0 || body.success === true)) {
                console.log(`[${screenName}] Success:`, body);
                return {
                    success: true,
                    data: body.data !== undefined ? body.data : body,
                    response: body,
                    status: response.status,
                    text: async () => JSON.stringify(body),
                };
            }

            // Structured error returned with 2xx status (legacy backends)
            const errMsg = (body && (body.message || body.error)) || 'API Error';
            console.error(`[${screenName}] Backend Error:`, errMsg);
            try {
                const lc = String(errMsg).toLowerCase();
                if (lc.includes("user doesn't exists") || lc.includes("user doesn't exist") || lc.includes('user does not exist')) {
                    const decoded = await getDecodedToken();
                    console.warn(`[${screenName}] Backend reported missing user — decoded token:`, decoded);
                }
            } catch (e) {
                console.warn('[Fetch] failed to decode token for error logging', e);
            }

            return {
                success: false,
                error: errMsg,
                response: body,
                status: response.status,
                text: async () => (typeof body === 'string' ? body : JSON.stringify(body)),
            };
        }

        // Non-2xx HTTP status codes
        const httpErrMsg = (body && (body.message || body.error)) || response.statusText || 'Server Error';
        console.error(`[${screenName}] HTTP Error ${response.status}:`, httpErrMsg);
        if (response.status >= 500) {
            console.error(`[${screenName}] Server-side error body:`, body);
        }
        try {
            const lc = String(httpErrMsg || '').toLowerCase();
            if (lc.includes("user doesn't exists") || lc.includes("user doesn't exist") || lc.includes('user does not exist')) {
                const decoded = await getDecodedToken();
                console.warn(`[${screenName}] Backend reported missing user — decoded token:`, decoded);
            }
        } catch (e) {
            console.warn('[Fetch] failed to decode token for error logging', e);
        }

        return {
            success: false,
            error: httpErrMsg,
            response: body || {},
            status: response.status,
            text: async () => (typeof body === 'string' ? body : JSON.stringify(body || httpErrMsg)),
        };

    } catch (error) {
        console.error(`[${screenName}] Fetch Error:`, error.message);
        
        // Network errors return consistent format
        return {
            success: false,
            error: error.message,
            status: 999
        };
    }
};
